// Treatment-plan setups: the 3D thing a doctor approves, and the changes they ask for.
//
// ── The bundle format ────────────────────────────────────────────────────────────────────────────
// A setup is a base mesh plus a per-step position for every tooth. That is what makes a viewer
// feasible: exporting a separate mesh for each step would be ~180 MB for a 30-step case, while a
// segmented mesh plus 4x4 matrices is a few MB and animates smoothly.
//
// The format below is Alignova's own, deliberately CAD-agnostic. ArchForm is the first importer;
// uLab, 3Shape and Blue Sky Plan can be added as further importers WITHOUT the viewer changing,
// because the viewer only ever reads this shape.
import { z } from 'zod';

/** FDI tooth notation ("11" upper right central, "48" lower left third molar). */
export type ToothId = string;

export const ARCHES = ['upper', 'lower'] as const;
export type Arch = (typeof ARCHES)[number];

/**
 * A tooth's placement at one step: a 4x4 column-major matrix, the same convention three.js and
 * glTF use, so it can be applied to a node without conversion.
 */
export type Matrix4 = number[];

export interface SetupStep {
  /** 0 is the starting position — what the patient's teeth look like today. */
  index: number;
  /** Only the teeth that MOVE need an entry; anything absent holds its previous position. */
  teeth: Record<ToothId, Matrix4>;
  /** Interproximal reduction performed before this step, in millimetres, keyed "11-12". */
  ipr?: Record<string, number>;
  /** Attachments present from this step onward. */
  attachments?: ToothId[];
}

/** What the importer produces and the viewer consumes. */
export interface SetupBundle {
  /** Format version, so an old bundle can still be read after the shape evolves. */
  version: 1;
  /** Which software produced it, for support ("this one came out of ArchForm 3.2"). */
  source: SetupSource;
  sourceVersion?: string | null;
  arches: Arch[];
  /** Step count including step 0. */
  stepCount: number;
  steps: SetupStep[];
  /** Teeth present in the mesh, so the viewer can label and pick them. */
  teeth: ToothId[];
  /** Millimetres per model unit, if the exporter states it. */
  unitScale?: number;
  notes?: string | null;
}

export const SETUP_SOURCES = ['archform', 'ulab', '3shape', 'blueskyplan', 'generic_glb', 'alignova_sim', 'unknown'] as const;
export type SetupSource = (typeof SETUP_SOURCES)[number];

export const SETUP_SOURCE_LABELS: Record<SetupSource, string> = {
  archform: 'ArchForm',
  ulab: 'uLab',
  '3shape': '3Shape Ortho System',
  blueskyplan: 'Blue Sky Plan',
  generic_glb: 'glTF / GLB',
  alignova_sim: 'Alignova simulation',
  unknown: 'Unrecognised',
};

/**
 * A setup as stored: the bundle's metadata plus where its mesh and step data live.
 * The heavy files are ordinary case files, so they reuse the upload, storage and Range-streaming
 * that already exist.
 */
export interface CaseSetup {
  id: string;
  caseId: string;
  /** r1, r2, r3 … the revision the doctor is looking at. Approval binds to this exact one. */
  revision: number;
  source: SetupSource;
  sourceVersion: string | null;
  stepCount: number;
  arches: Arch[];
  /** wf_case_files row holding the mesh (GLB). */
  meshFileId: string | null;
  /** wf_case_files row holding steps.json. */
  stepsFileId: string | null;
  /** True when this revision is an Alignova simulation package (per-stage meshes, Unity viewer). */
  hasSimulation: boolean;
  /** Integrity of what was shown — an approval names the bytes it approved. */
  bundleHash: string | null;
  status: SetupStatus;
  /** Signed share link, when the lab has made one. */
  shareToken: string | null;
  shareExpiresAt: string | null;
  notes: string | null;
  createdByName: string | null;
  createdAt: string;
  sharedAt: string | null;
  decidedAt: string | null;
  decidedByName: string | null;
}

export const SETUP_STATUSES = ['draft', 'shared', 'approved', 'changes_requested', 'superseded'] as const;
export type SetupStatus = (typeof SETUP_STATUSES)[number];

export const SETUP_STATUS_LABELS: Record<SetupStatus, string> = {
  draft: 'Draft',
  shared: 'With the doctor',
  approved: 'Approved',
  changes_requested: 'Changes requested',
  superseded: 'Superseded',
};

// ── Doctor changes ───────────────────────────────────────────────────────────────────────────────
//
// Two tiers, and deliberately NOT a third. Tier 1 is a note pinned to a tooth and a step; tier 2 is
// a structured request the technician applies in CAD. What is not offered is dragging teeth in the
// browser: a setup moved without root, collision and biomechanical checking has to be redone in the
// CAD anyway, so it would give the doctor the feeling of control without the substance.

/** Tier 2 — the adjustments a doctor actually asks for, as data rather than prose. */
export const SETUP_CHANGE_KINDS = [
  'hold_tooth', // do not move this tooth
  'more_ipr',
  'less_ipr',
  'more_torque',
  'less_torque',
  'more_rotation',
  'less_rotation',
  'extrude',
  'intrude',
  'add_attachment',
  'remove_attachment',
  'extend_steps', // slow it down
  'shorten_steps', // speed it up
  'other',
] as const;
export type SetupChangeKind = (typeof SETUP_CHANGE_KINDS)[number];

export const SETUP_CHANGE_LABELS: Record<SetupChangeKind, string> = {
  hold_tooth: 'Do not move this tooth',
  more_ipr: 'More IPR',
  less_ipr: 'Less IPR',
  more_torque: 'More torque',
  less_torque: 'Less torque',
  more_rotation: 'More rotation',
  less_rotation: 'Less rotation',
  extrude: 'Extrude',
  intrude: 'Intrude',
  add_attachment: 'Add an attachment',
  remove_attachment: 'Remove the attachment',
  extend_steps: 'Extend - more steps',
  shorten_steps: 'Shorten - fewer steps',
  other: 'Something else',
};

export interface SetupAnnotation {
  id: string;
  setupId: string;
  /** Tier 1 pins a note to a tooth and a step; tier 2 adds a `kind`. */
  toothId: ToothId | null;
  stepIndex: number | null;
  kind: SetupChangeKind | null;
  note: string | null;
  /** Millimetres or degrees, where the kind takes an amount. */
  amount: number | null;
  authorName: string | null;
  authorSide: 'lab' | 'clinic';
  resolvedAt: string | null;
  createdAt: string;
}

export const setupAnnotationSchema = z.object({
  toothId: z.string().trim().max(4).nullish(),
  stepIndex: z.coerce.number().int().min(0).max(400).nullish(),
  kind: z.enum(SETUP_CHANGE_KINDS).nullish(),
  note: z.string().trim().max(1000).nullish(),
  amount: z.coerce.number().min(-20).max(20).nullish(),
});
export type SetupAnnotationInput = z.infer<typeof setupAnnotationSchema>;

/** The doctor's verdict on a revision. */
export const setupDecisionSchema = z.object({
  decision: z.enum(['approved', 'changes_requested']),
  comment: z.string().trim().max(2000).nullish(),
});
export type SetupDecisionInput = z.infer<typeof setupDecisionSchema>;

/** Everything a viewer needs for one revision, in one fetch. */
export interface SetupView {
  setup: CaseSetup;
  caseNumber: string;
  patientName: string;
  doctorName: string | null;
  bundle: SetupBundle | null;
  annotations: SetupAnnotation[];
  /** URL for the mesh; the viewer streams it. */
  meshUrl: string | null;
  /**
   * Folder URL of an Alignova simulation package (manifest.json + .tsm meshes), ending in "/".
   * Set only for simulation revisions; each audience gets its own authorised route.
   */
  simulationUrl: string | null;
  /** True when the reader may approve or request changes (the treating doctor, not the lab). */
  canDecide: boolean;
}

// ── Movement summary ─────────────────────────────────────────────────────────────────────────────
//
// What the plan actually does to each tooth, in millimetres and degrees. This is the number a doctor
// checks the plan against — "you have 41 travelling 3.2 mm distally" is reviewable in a way that
// watching an animation is not.
//
// It lives here rather than in the viewer because the lab, the doctor and the server must all quote
// the same figure, and because it is pure arithmetic on the bundle: no three.js, no rendering.

export interface ToothMovement {
  toothId: ToothId;
  arch: Arch;
  /** Straight-line distance from the start position to the final one, in millimetres. */
  translationMm: number;
  /** Total rotation from start to finish, in degrees, about whatever axis it turns on. */
  rotationDeg: number;
  /** Steps at which this tooth's position changes — the ones that do the work. */
  activeSteps: number[];
  /** Interproximal reduction planned against this tooth, summed over the case, in millimetres. */
  iprMm: number;
  /** True if any step attaches something to it. */
  hasAttachment: boolean;
}

/** Column-major 4x4, the glTF/three.js convention: translation is elements 12, 13, 14. */
const translationOf = (m: Matrix4): [number, number, number] => [m[12] ?? 0, m[13] ?? 0, m[14] ?? 0];

/**
 * The rotation part, with scale divided out.
 *
 * Exporters differ on units — a plan in centimetres carries a 0.1 scale on every basis vector — and
 * an unnormalised matrix turns an angle calculation into nonsense, so the columns are normalised
 * before anything is read from them.
 */
function rotationOf(m: Matrix4): number[] {
  const col = (i: number): [number, number, number] => [m[i * 4] ?? 0, m[i * 4 + 1] ?? 0, m[i * 4 + 2] ?? 0];
  const out: number[] = [];
  for (let i = 0; i < 3; i++) {
    const c = col(i);
    const len = Math.hypot(c[0], c[1], c[2]) || 1;
    out.push(c[0] / len, c[1] / len, c[2] / len);
  }
  return out;
}

/**
 * Angle between two rotations, in degrees.
 *
 * From the trace of Rᵃᵀ·Rᵇ: for a rotation matrix the trace is 1 + 2cos θ, so θ falls straight out.
 * Clamped because floating-point drift can push the cosine a hair outside [-1, 1] and acos would
 * return NaN for a tooth that barely moved.
 */
function angleBetween(a: Matrix4, b: Matrix4): number {
  const ra = rotationOf(a);
  const rb = rotationOf(b);
  // trace(Raᵀ·Rb) — the dot product of the two bases, column by column.
  let trace = 0;
  for (let i = 0; i < 9; i++) trace += (ra[i] ?? 0) * (rb[i] ?? 0);
  const cos = Math.min(1, Math.max(-1, (trace - 1) / 2));
  return (Math.acos(cos) * 180) / Math.PI;
}

const archOf = (tooth: ToothId): Arch => (tooth.startsWith('1') || tooth.startsWith('2') ? 'upper' : 'lower');

/**
 * Per-tooth movement over the whole plan.
 *
 * A step only lists the teeth that move, so positions are carried forward: a tooth's placement at
 * step 12 is whatever the last step that mentioned it said.
 */
export function summariseMovement(bundle: SetupBundle): ToothMovement[] {
  const steps = [...bundle.steps].sort((a, b) => a.index - b.index);
  const first = new Map<ToothId, Matrix4>();
  const last = new Map<ToothId, Matrix4>();
  const active = new Map<ToothId, number[]>();
  const attached = new Set<ToothId>();
  const ipr = new Map<ToothId, number>();

  for (const step of steps) {
    for (const tooth of step.attachments ?? []) attached.add(tooth);
    for (const [pair, mm] of Object.entries(step.ipr ?? {})) {
      // "11-12" reduces enamel on both neighbours; each carries the planned amount.
      for (const t of pair.split('-')) {
        const key = t.trim();
        if (key) ipr.set(key, (ipr.get(key) ?? 0) + mm);
      }
    }
    for (const [tooth, m] of Object.entries(step.teeth)) {
      if (!Array.isArray(m) || m.length !== 16) continue;
      if (!first.has(tooth)) {
        first.set(tooth, m);
      } else {
        const prev = last.get(tooth) ?? first.get(tooth)!;
        // Only count a step as active if the tooth is actually somewhere new. Some exporters repeat
        // an unchanged matrix on every step, which would otherwise read as movement everywhere.
        const moved = m.some((v, i) => Math.abs(v - (prev[i] ?? 0)) > 1e-6);
        if (moved) active.set(tooth, [...(active.get(tooth) ?? []), step.index]);
      }
      last.set(tooth, m);
    }
  }

  const scale = bundle.unitScale && bundle.unitScale > 0 ? bundle.unitScale : 1;
  const teeth = bundle.teeth.length ? bundle.teeth : [...first.keys()];
  return teeth
    .filter((t) => first.has(t))
    .map((toothId) => {
      const a = first.get(toothId)!;
      const b = last.get(toothId) ?? a;
      const [ax, ay, az] = translationOf(a);
      const [bx, by, bz] = translationOf(b);
      return {
        toothId,
        arch: archOf(toothId),
        translationMm: Math.hypot(bx - ax, by - ay, bz - az) * scale,
        rotationDeg: angleBetween(a, b),
        activeSteps: active.get(toothId) ?? [],
        iprMm: ipr.get(toothId) ?? 0,
        hasAttachment: attached.has(toothId),
      };
    })
    .sort((x, y) => x.toothId.localeCompare(y.toothId));
}

/** Teeth whose position changes at this exact step — what the viewer highlights during playback. */
export function movingAtStep(bundle: SetupBundle, index: number): Set<ToothId> {
  const steps = [...bundle.steps].sort((a, b) => a.index - b.index);
  const pos = new Map<ToothId, Matrix4>();
  const moving = new Set<ToothId>();
  for (const step of steps) {
    if (step.index > index) break;
    for (const [tooth, m] of Object.entries(step.teeth)) {
      if (!Array.isArray(m) || m.length !== 16) continue;
      const prev = pos.get(tooth);
      if (step.index === index && prev && m.some((v, i) => Math.abs(v - (prev[i] ?? 0)) > 1e-6)) {
        moving.add(tooth);
      }
      pos.set(tooth, m);
    }
  }
  return moving;
}
